import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, map, find, cloneDeep } from 'lodash';
import turf from '@turf/turf';
import uuid from 'uuid/v1';
import { Bert } from 'meteor/themeteorchef:bert';

import { Stack, Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../../Loading';
import handleErrorInClient from '../../../../modules/handle-error-in-client';

import { ELEMENT_TYPES } from '../../../../api/AnnotationTemplates/AnnotationTemplates';
import * as templateOps from '../../../../api/AnnotationTemplates/annotation-template-operations';
import * as annotationOps from '../../../../api/Annotations/annotation-operations';

import UploadFileComponent from '../../UploadFileComponent/UploadFileComponent';
import useTimeStore from '../../ZustandProvider';

import {
  renderTable,
  renderButton,
  renderSelect,
  renderSwitch,
  renderIconButton,
  renderRadioButton
} from '../../MaterialMUIComponent';

const IMAGE_DEFAULT = 'https://cdn-icons-png.flaticon.com/512/9218/9218848.png';
const TemplatePointCloudItemEdit = ({
  elementItem,
  updateAnnotationFields,
  serverConnection,
  updatedAnnotation,
  selectedAnnotation
}) => {
  const coordOnMap = useTimeStore(state => state.coordOnMap);
  const multiPointCloudSchema = templateOps.getElementMultiplePointCloud(elementItem);
  const isMultiPoint = isEqual(
    templateOps.getElementType(elementItem),
    ELEMENT_TYPES.MULTIPLEPOINTCLOUD
  );
  const schemaDefault = {
    server: {},
    source: {},
    connector: {}
  };
  const startSchema = isMultiPoint ? {} : templateOps.getElementConnections(elementItem);
  const startNewSchema = !isEmpty(startSchema) ? startSchema : schemaDefault;
  const [newSchema, setNewSchema] = React.useState(startNewSchema);
  const [sources, setSources] = React.useState([]);
  const [isLoadingCloud, setIsLoadingCloud] = React.useState(false);
  const indexMultiPointCloud = schema =>
    multiPointCloudSchema.indexOf(
      find(multiPointCloudSchema, item => isEqual(item.source.name, schema.source.name))
    );

  const urlFunction = server => {
    let url = '';
    if (server.serverType === 'IGP') {
      url = `http://${server.ip}:${server.port}/MG/lidar/write_lidar_html`;
    } else if (server.serverType === 'IGPS' || server.serverType === 'input') {
      url = `${server.domain}/MG/lidar/write_lidar_html`;
    }
    return url;
  };

  const coordsFunction = () => {
    let coords = [];
    if (!isEmpty(updatedAnnotation)) coords = updatedAnnotation.geoJSON.geometry.coordinates;
    else if (!isEmpty(selectedAnnotation)) {
      const geojson = annotationOps.getGeoJSON(selectedAnnotation);
      coords = turf.getCoord(geojson);
    } else if (!isEmpty(coordOnMap)) {
      const { lat, lng } = coordOnMap;
      coords = [lng, lat];
    } else Bert.alert('Coordinates not defined', 'danger');
    return coords;
  };

  const addPointCloudList = (fastSchema = {}) => {
    const cloneMultiSchema = cloneDeep(multiPointCloudSchema);
    const cloneNewSchema = isEmpty(fastSchema) ? cloneDeep(newSchema) : cloneDeep(fastSchema);
    if (!isEqual(indexMultiPointCloud(cloneNewSchema), -1)) {
      cloneMultiSchema.splice(indexMultiPointCloud(cloneNewSchema), 1, cloneNewSchema);
      updateAnnotationFields(elementItem, 'multiplePointCloud', cloneMultiSchema);
    } else
      updateAnnotationFields(elementItem, 'multiplePointCloud', [
        ...multiPointCloudSchema,
        cloneNewSchema
      ]);
    Bert.alert('Map add/update successfuly', 'success');
  };

  const deletePointCloudList = () => {
    const cloneMultiSchema = cloneDeep(multiPointCloudSchema);
    if (!isEqual(indexMultiPointCloud(newSchema), -1))
      cloneMultiSchema.splice(indexMultiPointCloud(newSchema), 1);
    updateAnnotationFields(elementItem, 'multiplePointCloud', cloneMultiSchema);
    Bert.alert('Map remove successfuly', 'warning');
  };

  const handleServer = async serverName => {
    if (!isEqual(serverName, 'none')) {
      const serverSelected = find(serverConnection, element => isEqual(element.name, serverName));
      try {
        const response = await fetch(urlFunction(serverSelected), {
          method: 'POST',
          body: JSON.stringify({
            ftpIp: '',
            ftpPort: '',
            ftpUser: '',
            ftpPassword: '',
            httpPort: '',
            disc: 'data3',
            projectName: 'Request',
            projectPath: '',
            projectsEntwine: '',
            position: ''
          }),
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        });
        const sourcesAnswer = await response.json();
        setSources(sourcesAnswer);
        setNewSchema({ ...newSchema, server: serverSelected });
      } catch (error) {
        handleErrorInClient(error);
      }
    } else {
      setSources([]);
      setNewSchema({ ...newSchema, source: {}, server: {} });
    }
  };

  const callBackPointCloud = async sourceSelected => {
    if (!isEqual(sourceSelected, 'none')) {
      const newSource = cloneDeep(find(sources, item => isEqual(item.name, sourceSelected)));
      const coords = coordsFunction();
      if (!isEmpty(coords)) {
        try {
          const response = await fetch(urlFunction(newSchema.server), {
            method: 'POST',
            body: JSON.stringify({
              disc: 'data3',
              ftpIp: '',
              ftpPort: '',
              ftpUser: '',
              ftpPassword: '',
              httpPort: '',
              projectsEntwine: [newSource.path],
              projectName: '',
              projectPath: 'htmls/lidar/tempwines',
              position: [coords[1], coords[0]],
              user: ''
            }),
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors',
            credentials: 'omit',
            referrerPolicy: 'no-referrer'
          });

          const answer = await response.json();
          if (!isEmpty(answer)) {
            const pointCloudLink = answer.htmlCreated;
            const newsource = {
              ...newSchema.source,
              link: pointCloudLink,
              name: sourceSelected,
              imgLink: IMAGE_DEFAULT
            };
            const schema = {
              ...newSchema,
              source: newsource
            };
            setNewSchema(schema);
            if (!isMultiPoint) updateAnnotationFields(elementItem, 'connections', schema);
          }
          Bert.alert('Point Cloud created successfuly', 'success');
        } catch (error) {
          Bert.alert(
            'The coordinates not defined, it is something error with annotation',
            'danger'
          );
          handleErrorInClient(error);
        }
      }
    } else {
      setNewSchema({ ...newSchema, source: {} });
      if (!isMultiPoint) updateAnnotationFields(elementItem, 'connections', {});
    }
    setIsLoadingCloud(false);
  };

  const handleSchemaList = nameSchema => {
    if (isEqual(nameSchema, 'New...')) setNewSchema(schemaDefault);
    else {
      const pointCloudSchema = find(multiPointCloudSchema, item =>
        isEqual(item.source.name, nameSchema)
      );
      setNewSchema(pointCloudSchema);
    }
  };

  const iconsPosition = [
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'flex-start'),
      icon: <FontAwesomeIcon icon="align-left" />,
      title: 'Align left',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'flex-start' ? 'green' : 'blue'
    },
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'center'),
      icon: <FontAwesomeIcon icon="align-justify" />,
      title: 'Center',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'center' ? 'green' : 'blue'
    },
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'flex-end'),
      icon: <FontAwesomeIcon icon="align-right" />,
      title: 'Align right',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'flex-end' ? 'green' : 'blue'
    }
  ];

  const buttonsSaveRemove = [
    {
      functionProp: () => addPointCloudList(),
      contentProp: !isEmpty(
        find(multiPointCloudSchema, item => isEqual(item.source.name, newSchema.source.name))
      )
        ? 'Update '
        : 'Add',
      disabledProp: isEmpty(newSchema.source),
      colorProp: 'success',
      styleProp: {}
    },
    {
      functionProp: () => deletePointCloudList(),
      contentProp: 'Remove',
      disabledProp: isEmpty(
        find(multiPointCloudSchema, item => isEqual(item.source.name, newSchema.source.name))
      ),
      colorProp: 'error',
      styleProp: {}
    }
  ];

  const elementsTable = [
    {
      row: 1,
      column: 0,
      isShowRow: true,
      isShowCol: true,
      showElement: true,
      rowJustifyContent: 'flex-start',
      elements: [
        isMultiPoint && (
          <Stack key={uuid()} direction="row">
            {renderSwitch({
              key: uuid(),
              checked: newSchema.onlyFullScreen,
              onChange: event =>
                setNewSchema({ ...newSchema, onlyFullScreen: event.target.checked }),
              labelCheck: 'Full Screen',
              labelUncheck: 'Inside Annotation'
            })}
            {map(iconsPosition, iconElement =>
              renderIconButton({
                key: uuid(),
                style: { color: iconElement.color },
                onClick: iconElement.functionProp,
                icon: iconElement.icon,
                titleTooltip: iconElement.title,
                disabled: iconElement.disabled
              })
            )}
          </Stack>
        )
      ]
    },
    {
      row: 2,
      column: 1,
      isShowRow: true,
      isShowCol: true,
      colWidth: '100vh',
      showElement: true,
      elements: [
        isMultiPoint && (
          <>
            <UploadFileComponent
              key={uuid()}
              linkValueProp={isEmpty(newSchema.source) ? IMAGE_DEFAULT : newSchema.source.imgLink}
              handleValueLink={link => {
                const newSource = { ...newSchema.source, imgLink: link };
                setNewSchema({ ...newSchema, source: newSource });
              }}
            />
          </>
        ),
        renderSelect({
          key: !isEmpty(newSchema.server) ? newSchema.server.name : 'select-server',
          style: {
            marginTop: '1/2%',
            width: '95%'
          },
          label: 'Server Connection',
          inputLabel: 'Server Connection',
          arrayOptions: ['none'].concat(map(serverConnection, itemServer => itemServer.name)),
          onChange: event => handleServer(event.target.value),
          value: !isEmpty(newSchema.server) ? newSchema.server.name : 'none'
        }),
        !isEmpty(sources) && (
          <Box key={uuid()} width="100%" maxHeight="30vh" overflow="auto">
            <Stack key={uuid()} direction="row" width="100%">
              <Typography
                key={uuid()}
                style={{ marginTop: '2%', marginRight: '2%', fontWeight: 'bold' }}
                variant="body1"
              >
                Sources
              </Typography>
              {isLoadingCloud && <Loading key={uuid()} />}
            </Stack>
            {renderRadioButton({
              key: !isEmpty(newSchema.source) ? newSchema.source.name : 'radioButton-source',
              isRow: false,
              arrayOptions: ['none'].concat(map(sources, itemSource => itemSource.name)),
              onChange: source => {
                setIsLoadingCloud(true);
                callBackPointCloud(source);
              },
              valueChecked: !isEmpty(newSchema.source) ? newSchema.source.name : 'none'
            })}
          </Box>
        )
      ]
    },
    {
      row: 2,
      column: 2,
      isShowRow: true,
      isShowCol: true,
      showElement: true,
      elements: [
        isMultiPoint &&
          renderSelect({
            key: uuid(),
            multiple: true,
            style: {
              width: '95%',
              marginTop: '1/2%'
            },
            label: 'IMaps',
            inputLabel: 'IMaps',
            arrayOptions: ['New...'].concat(map(multiPointCloudSchema, item => item.source.name)),
            onChange: event => handleSchemaList(event.target.value),
            value: !isEmpty(
              find(multiPointCloudSchema, item => isEqual(item.source.name, newSchema.source.name))
            )
              ? [newSchema.source.name]
              : ['New...']
          }),
        isMultiPoint && (
          <Stack
            key={uuid()}
            direction="row"
            display="flex"
            justifyContent="flex-end"
            marginTop="2%"
            marginRight="2.5%"
          >
            {map(buttonsSaveRemove, item =>
              renderButton({
                key: uuid(),
                onClick: item.functionProp,
                contentButton: item.contentProp,
                disabled: item.disabledProp,
                color: item.colorProp,
                style: item.styleProp
              })
            )}
          </Stack>
        ),
        !isMultiPoint && !isEmpty(newSchema.source) && (
          <iframe
            key={uuid()}
            title="WebPage"
            src={newSchema.source.link}
            style={{ width: '100%', height: '30vh' }}
          />
        )
      ]
    }
  ];

  React.useEffect(() => {
    if (!isEmpty(elementItem.connections)) {
      const { server } = elementItem.connections;
      handleServer(server.name);
    }
  }, [elementItem]);
  return (
    <>
      <Box width="100%" id="main-box">
        <Box width="100%" id="box-component">
          {renderTable(elementsTable)}
        </Box>
      </Box>
    </>
  );
};
TemplatePointCloudItemEdit.defaultProps = {
  serverConnection: [],
  updatedAnnotation: {},
  selectedAnnotation: {}
};

TemplatePointCloudItemEdit.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func.isRequired,
  serverConnection: PropTypes.array,
  updatedAnnotation: PropTypes.object,
  selectedAnnotation: PropTypes.object
};

export default TemplatePointCloudItemEdit;
